import {
  Avatar,
  Box,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import {
  InviteSectionCancelRequestBtn,
  InviteSectionMemberCard,
  InviteSectionStatusBadge,
  InviteSectionToggleButton,
  InviteSectionToggleOption,
  SeatContainer,
  SeatCountBox,
  SeatStatusBadge,
  SeatStyledButton,
} from "../../pageComponents/settings/style";
import { StyledDivider } from "../../globalStyle";
import { HeaderSubTitle } from "../../pageComponents/style";
import { useDispatch, useSelector } from "react-redux";
import { MetaDataSagaActions } from "../../store/metadata/sagas";
import {
  seatSeactionLoaderSelector,
  teamMemberSeatListSelector,
} from "../../store/metadata/selector";
import { useEffect, useState } from "react";
import AssignMemberSeatModal from "../modals/assignMemberSeatModal";
import { MetaDataReducerAction } from "../../store/metadata/slice";
import PurchaseNewSeatModal from "../modals/purchaseNewSeatModal";
import DeleteConfirmationModal from "../modals/deleteConfirmationModal";
import { PaymentSagaActions } from "../../store/payment/sagas";
import { activeSubscriptionSelector } from "../../store/payment/selector";

const topCardStyle = {
  flex: 1,
  padding: "1rem",
  borderRadius: "0.5rem",
  backgroundColor: "#FFFFFF",
  color: "#1A1A1A",
  cursor: "pointer",
  border: "1px solid transparent",
  boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.05)",
  transition: "all 0.3s ease",
};

const selectedStyle = {
  borderColor: "#2196f3",
  backgroundColor: "rgba(33, 150, 243, 0.08)",
};

const infoCardStyle = {
  flex: 1,
  padding: "1rem",
  borderRadius: "0.5rem",
  backgroundColor: "#FFFFFF",
  color: "#1A1A1A",
};

export const InviteMemberSection = () => {
  // Selectors

  const dispatch = useDispatch();
  const teamMemberSeatList = useSelector(teamMemberSeatListSelector);
  const seatSeactionLoader = useSelector(seatSeactionLoaderSelector);
  const activePlan = useSelector(activeSubscriptionSelector);
  const [isFreeSeatOverAbovePlan, setIsFreeSeatOverAbovePlan] = useState([]);
  const [removeMember, setRemoveMember] = useState<string | null>(null);
  const [removeSeat, setRemoveSeat] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<"paid" | "free">("free");

  useEffect(() => {
    dispatch(MetaDataSagaActions.getInviteMembersList());
    dispatch(MetaDataSagaActions.getTeamMembersSeatListSaga());
    dispatch(PaymentSagaActions.getActiveSubscription());
  }, [dispatch]);
  useEffect(() => {
    if (
      activePlan?.activePlanDetails &&
      activePlan?.activePlanDetails?.freeSeat > 0 &&
      teamMemberSeatList?.seatBlock?.length > 0
    ) {
      const freeSeats = teamMemberSeatList?.seatBlock?.filter(
        (seat: any) => seat?.isFree
      );

      if (freeSeats.length > activePlan?.activePlanDetails?.freeSeat) {
        setIsFreeSeatOverAbovePlan(freeSeats);
      }
      if (freeSeats.length === 0) {
        setIsFreeSeatOverAbovePlan([]);
      }
    }
  }, [teamMemberSeatList, activePlan]);

  const handleRemoveSeat = () => {
    dispatch(
      MetaDataSagaActions.removeSeatMember({
        seatId: removeMember,
        closeModal: () => setRemoveMember(null),
      })
    );
  };

  const handleCancelSeatRequest = (seatId: string) => {
    dispatch(
      MetaDataSagaActions.cancelSeatRequest({
        seatId: seatId,
      })
    );
  };

  const handleDisableSeat = (seatId: string) => {
    dispatch(
      MetaDataSagaActions.disableSeat({
        seatId: seatId,
      })
    );
  };

  const handleEnableSeat = (seatId: string) => {
    dispatch(
      MetaDataSagaActions.enableSeat({
        seatId: seatId,
      })
    );
  };

  const handleSubmitChooseFreeSeats = (seatId: string) => {
    setRemoveSeat(seatId);

    dispatch(
      MetaDataSagaActions.chooseFreeSeats({
        seatId,
        callback: () => {
          setRemoveSeat(null);
        },
      })
    );
  };

  const getMemberStatus = (status?: string) => {
    switch (status) {
      case "ACCEPTED":
        return "Accepted";
      case "PENDING":
        return "Pending";
      case "REJECTED":
        return "Rejected";
      default:
        return "";
    }
  };

  return (
    <Box sx={{ bgcolor: "#FAFAFA", padding: "30px" }}>
      {isFreeSeatOverAbovePlan?.length ? null : (
        <SeatContainer>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Available Seats -
            </Typography>
            <SeatCountBox>
              {teamMemberSeatList?.totalAvailableSeat ?? 0}
            </SeatCountBox>
          </Box>

          <SeatStyledButton
            onClick={() =>
              dispatch(MetaDataReducerAction.setOpenPurchaseSeatModal(true))
            }
          >
            Purchase Additional Seats
          </SeatStyledButton>
        </SeatContainer>
      )}
      {isFreeSeatOverAbovePlan?.length ? (
        <SeatContainer
          sx={{
            border: "1px solid #ffe3e7",
            background: "#fff0f2",
            borderRadius: "0.5rem",
            padding: "1rem",
            width: "100%",
          }}
        >
          <Box
            display="flex"
            alignItems="flex-start"
            gap={1}
            flexDirection={"column"}
          >
            <p style={{ color: "#1976d2" }}>
              Your current plan allows only{" "}
              {activePlan?.activePlanDetails?.freeSeat} out of{" "}
              {isFreeSeatOverAbovePlan?.length} members.
            </p>

            <Typography
              fontSize="0.875rem"
              fontWeight={500}
              color="#EB001B"
              sx={{ mt: 1 }}
            >
              ⚠️ Members not selected will temporarily lose access until this is
              completed.
            </Typography>
          </Box>
          <StyledDivider sx={{ margin: "1rem 0 0.75rem 0" }} />

          <Box
            sx={{
              width: "100%",
              maxHeight: "30rem",
              padding: "0.625rem",
              borderRadius: "0.625rem",
              overflowY: "auto",
              "::-webkit-scrollbar": { width: "4px" },
            }}
          >
            {isFreeSeatOverAbovePlan?.map((member: any, index: number) => {
              return (
                <InviteSectionMemberCard key={index}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      rowGap: "0.5rem",
                    }}
                  >
                    {/* User Info */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "1rem",
                        flex: 1,
                      }}
                    >
                      <Avatar
                        src={
                          member?.profileImage || "/assets/images/user-logo.png"
                        }
                        sx={{ width: 40, height: 40 }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={500} fontSize="1rem" noWrap>
                          {member?.fullName || `Seat ${index + 1}`}
                        </Typography>
                        <Typography fontSize="0.875rem" color="#7D7D7D" noWrap>
                          {member?.userEmail}
                        </Typography>
                      </Box>
                      <SeatStatusBadge isFreeSeat={true}>Free</SeatStatusBadge>
                      <SeatStatusBadge isFreeSeat={false}>
                        Disabled
                      </SeatStatusBadge>
                    </Box>
                  </Box>
                  <InviteSectionCancelRequestBtn
                    sx={{ paddingInline: "1.875rem", alignSelf: "flex-end" }}
                    onClick={() => handleSubmitChooseFreeSeats(member?._id)}
                    disabled={
                      seatSeactionLoader?.type === "chooseSeat" &&
                      seatSeactionLoader?.loading
                    }
                    startIcon={
                      seatSeactionLoader?.type === "chooseSeat" &&
                      seatSeactionLoader?.loading &&
                      removeSeat === member?._id && (
                        <CircularProgress size={20} color="inherit" />
                      )
                    }
                  >
                    Remove
                  </InviteSectionCancelRequestBtn>
                </InviteSectionMemberCard>
              );
            })}
          </Box>
        </SeatContainer>
      ) : teamMemberSeatList?.seatBlock?.length ? (
        <Stack marginTop={"1.8rem"}>
          <Stack
            direction="row"
            spacing={2}
            mb={3}
            sx={{
              flexWrap: "wrap",
              borderRadius: "0.5rem",
              border: "1px solid #E0E0E0",
              padding: "1rem",
            }}
          >
            <Box
              sx={{
                ...topCardStyle,
                ...(selectedTab === "free" ? selectedStyle : {}),
              }}
              onClick={() => setSelectedTab("free")}
            >
              <Typography fontSize="0.875rem" color="#B3B3B3">
                Free seats
              </Typography>
              <Typography fontSize="1.25rem" fontWeight={600}>
                {teamMemberSeatList?.seatBlock?.filter(
                  (member: any) => member?.isFree
                )?.length || 0}
              </Typography>
            </Box>

            <Box
              sx={{
                ...topCardStyle,
                ...(selectedTab === "paid" ? selectedStyle : {}),
              }}
              onClick={() => setSelectedTab("paid")}
            >
              <Typography fontSize="0.875rem" color="#B3B3B3">
                Paid seats
              </Typography>
              <Typography fontSize="1.25rem" fontWeight={600}>
                {teamMemberSeatList?.seatBlock?.filter(
                  (member: any) => !member?.isFree
                )?.length || 0}
              </Typography>
            </Box>

            <Box sx={infoCardStyle}>
              <Typography fontSize="0.875rem" color="#B3B3B3">
                Next billing period
              </Typography>
              <Typography fontSize="1.25rem" fontWeight={600}>
                {new Date(activePlan?.planEndDate).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </Typography>
            </Box>
          </Stack>
          <HeaderSubTitle>Invitations</HeaderSubTitle>
          <StyledDivider sx={{ margin: "1.25rem 0 0.938rem 0" }} />
          <Box
            sx={{
              width: "100%",
              maxHeight: "30rem",
              padding: "0.625rem",
              borderRadius: "0.625rem",
              overflowY: "auto",
              overflowX: "hidden",
              "::-webkit-scrollbar": {
                width: "4px",
              },
            }}
          >
            {teamMemberSeatList?.seatBlock
              ?.filter(
                (member: any) => (selectedTab === "free") === member?.isFree
              )
              .map((member: any, index: number) => {
                const isAccepted = member.acceptedStatus === "ACCEPTED";
                const isPending = member.acceptedStatus === "PENDING";
                const isRejected = member.acceptedStatus === "REJECTED";
                const memberStatus = getMemberStatus(member?.acceptedStatus);
                const isEnabled = member?.isEnable;
                const isFreeSeat = member?.isFree;

                let statusColor = "#1AA86E"; // default green
                let statusBg = "#E8F9F1";

                if (isPending) {
                  statusColor = "#FFA92B";
                  statusBg = "rgba(255,169,43,0.1)";
                } else if (isRejected) {
                  statusColor = "#EB001B";
                  statusBg = "#FDEDED";
                }

                return (
                  <InviteSectionMemberCard key={index}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        rowGap: "0.5rem",
                        width: "100%",
                      }}
                    >
                      {/* Member Info */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flex: "1 1 auto",
                          gap: "0.875rem",
                          minWidth: 0,
                          flexWrap: "wrap",
                        }}
                      >
                        <Avatar
                          src={
                            member?.profileImage ||
                            "assets/images/user-logo.png"
                          }
                          sx={{ width: "2.5rem", height: "2.5rem" }}
                        />

                        <Box
                          sx={{
                            flexShrink: 1,
                            minWidth: "14rem",
                            maxWidth: "14rem",
                          }}
                        >
                          <Typography
                            fontSize="1rem"
                            fontWeight={500}
                            color="#191919"
                            sx={{
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {member?.fullName || `Seat ${index + 1}`}
                          </Typography>
                          <Typography
                            fontSize="0.875rem"
                            color="#7D7D7D"
                            sx={{
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {member?.userEmail}
                          </Typography>
                        </Box>

                        <SeatStatusBadge isFreeSeat={isFreeSeat}>
                          {isFreeSeat ? "Free" : "Paid"}
                        </SeatStatusBadge>

                        {memberStatus?.length ? (
                          <InviteSectionStatusBadge
                            bg={statusBg}
                            color={statusColor}
                          >
                            {memberStatus}
                          </InviteSectionStatusBadge>
                        ) : null}
                      </Box>

                      {/* Action Buttons */}
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: "0.5rem",
                          flexShrink: 0,
                        }}
                      >
                        {isPending ? (
                          <InviteSectionCancelRequestBtn
                            onClick={() => handleCancelSeatRequest(member?._id)}
                            disabled={
                              seatSeactionLoader?.type === "cancelSeat" &&
                              seatSeactionLoader?.loading
                            }
                          >
                            {seatSeactionLoader?.type === "cancelSeat" &&
                            seatSeactionLoader?.loading ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  minWidth: "120px",
                                }}
                              >
                                <CircularProgress size={20} color="inherit" />
                              </Box>
                            ) : (
                              "Cancel Request"
                            )}
                          </InviteSectionCancelRequestBtn>
                        ) : (
                          <>
                            {isEnabled && isAccepted ? null : (
                              <SeatStyledButton
                                onClick={() =>
                                  dispatch(
                                    MetaDataReducerAction.setOpenAssignSeatModal(
                                      member?._id
                                    )
                                  )
                                }
                              >
                                {!isEnabled && isAccepted
                                  ? "Re-assign Seat"
                                  : "Assign Seat"}
                              </SeatStyledButton>
                            )}

                            {memberStatus?.length && isAccepted ? (
                              <InviteSectionToggleButton>
                                <InviteSectionToggleOption
                                  active={isEnabled}
                                  onClick={() => handleEnableSeat(member?._id)}
                                  disabled={
                                    (seatSeactionLoader?.type ===
                                      "enableSeat" &&
                                      seatSeactionLoader?.loading) ||
                                    isEnabled
                                  }
                                >
                                  {seatSeactionLoader?.type === "enableSeat" &&
                                  seatSeactionLoader?.loading ? (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        minWidth: "65px",
                                      }}
                                    >
                                      <CircularProgress
                                        size={20}
                                        color="inherit"
                                      />
                                    </Box>
                                  ) : isEnabled ? (
                                    "Enabled"
                                  ) : (
                                    "Re-enable"
                                  )}
                                </InviteSectionToggleOption>
                                <InviteSectionToggleOption
                                  active={!isEnabled}
                                  red={true}
                                  onClick={() => handleDisableSeat(member?._id)}
                                  disabled={
                                    (seatSeactionLoader?.type ===
                                      "disableSeat" &&
                                      seatSeactionLoader?.loading) ||
                                    !isEnabled
                                  }
                                >
                                  {seatSeactionLoader?.type === "disableSeat" &&
                                  seatSeactionLoader?.loading ? (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        minWidth: "65px",
                                      }}
                                    >
                                      <CircularProgress
                                        size={20}
                                        color="inherit"
                                      />
                                    </Box>
                                  ) : (
                                    "Disable"
                                  )}
                                </InviteSectionToggleOption>
                              </InviteSectionToggleButton>
                            ) : null}

                            {!isFreeSeat && !isEnabled && (
                              <InviteSectionCancelRequestBtn
                                onClick={() => setRemoveMember(member?._id)}
                                disabled={removeMember === member?._id}
                              >
                                Remove
                              </InviteSectionCancelRequestBtn>
                            )}
                          </>
                        )}
                      </Box>
                    </Box>
                  </InviteSectionMemberCard>
                );
              })}
          </Box>
        </Stack>
      ) : null}
      <AssignMemberSeatModal />
      <PurchaseNewSeatModal />
      <DeleteConfirmationModal
        handleDelete={handleRemoveSeat}
        handleClose={() => setRemoveMember(null)}
        isOpen={!!removeMember}
        btnLabel={"Confirm"}
        loading={
          seatSeactionLoader?.type === "removeSeat" &&
          seatSeactionLoader?.loading
        }
        modalTitle="Do you want to remove this seat?"
        modalDescription="If you confirmed then you will be not charged for this seat in next subscriotion cycle. Are you sure you want to proceed?"
      />
    </Box>
  );
};
